import type { FragmentType } from "@repo/graphql-types/fragment-masking";
import { useFragment } from "@repo/graphql-types/fragment-masking";
import { graphql } from "@repo/graphql-types/gql";
import { formatCurrency } from "@repo/lib";
import { CpsBadge } from "corpus";
import { getPaymentsPaidAndPendingAmounts } from "@/lib/payments";

export const WaitingRoomAppointmentListItemPaymentFragment = graphql(/* GraphQL */ `
  fragment WaitingRoomAppointmentListItemPaymentFragment on agendamentos {
    codAgendamento
    pago
    pagar
    valor
    onlinePayments(where: { status: { _neq: "canceled" } }) {
      codAgendamento
      amount
      status
    }
    agendamentosVinculados: AgendamentosVinculados(
      where: { codAgendamentoTipoVinculo: { _eq: 2 } }
    ) {
      onlinePayments(where: { status: { _neq: "canceled" } }) {
        codAgendamento
        amount
        status
      }
    }
  }
`);

interface WaitingRoomAppointmentListItemPaymentProps {
  data: FragmentType<typeof WaitingRoomAppointmentListItemPaymentFragment>;
}

export const WaitingRoomAppointmentListItemPayment = ({
  data,
}: WaitingRoomAppointmentListItemPaymentProps): JSX.Element => {
  const appointment = useFragment(WaitingRoomAppointmentListItemPaymentFragment, data);

  const { pagar, pago, valor, onlinePayments, agendamentosVinculados } = appointment;

  const relatedAppointmentsOnlinePayments = agendamentosVinculados.flatMap(
    (item) => item.onlinePayments,
  );

  const hasPayment =
    pagar || onlinePayments.length > 0 || relatedAppointmentsOnlinePayments.length > 0;

  const buildContent = (): JSX.Element | null => {
    if (!hasPayment) return null;

    const { totalPaid, totalPending } = getPaymentsPaidAndPendingAmounts(
      pagar,
      valor,
      pago,
      onlinePayments,
      relatedAppointmentsOnlinePayments,
    );

    return (
      <div className="flex flex-row gap-2 items-end">
        {totalPaid > 0 &&
          paymentBadge({ label: "Pago", color: "success", amount: totalPaid })}
        {totalPending > 0 &&
          paymentBadge({ label: "Em aberto", color: "helper", amount: totalPending })}
      </div>
    );
  };

  const paymentBadge = ({
    label,
    color,
    amount,
  }: {
    label: string;
    color: "success" | "helper";
    amount: number;
  }): JSX.Element => (
    <div className="flex text-xs justify-end w-full">
      <div className="flex flex-row gap-x-2">
        <CpsBadge color={color}>{label}</CpsBadge>
        <div className="flex items-center ml-auto min-w-[4.5rem] text-right">
          <span className="text-neutral-400">R$</span>
          <p>{formatCurrency(amount).replace("R$", "")}</p>
        </div>
      </div>
    </div>
  );

  return <>{buildContent()}</>;
};
